import { localizationService } from "../../localization";
import { Renderable } from "../renderable/Renderable";
import { TextAlign, TextAnchorX, TextAnchorY, FontStyle, FontWeight } from "flavr-common";
import { Text } from "troika-three-text";
// Documentation: https://protectwise.github.io/troika/troika-three-text/
export class TextRenderable extends Renderable {
    constructor() {
        super(...arguments);
        this.type = TextRenderable.typestatic;
    }
    destroy() {
        if (this.text) {
            this.owner.removeObjectFromScene(this.text);
            this.text.geometry.dispose();
            if (!Array.isArray(this.text.material)) {
                this.text.material.dispose();
            }
            this.text = undefined;
        }
    }
    setVisible(visible) {
        if (this.text)
            this.text.visible = visible;
    }
    updateFacet(facet) {
        if (this.text && this.facet) {
            // the fadeOut flag has changed, therfor we have to use a different InstanceBatch
            this.destroy();
        }
        if (!this.text) {
            this.text = new Text();
        }
        this.applyParameters(facet);
        this.text.position.copy(this.owner.worldPosition);
        this.text.quaternion.copy(this.owner.worldOrientation);
        this.text.scale.copy(this.owner.worldScale);
        this.text.rotateY(Math.PI);
        this.text.updateMatrix();
        this.text.sync();
        this.owner.addObjectToScene(this.text);
    }
    updateTransform() {
        if (!this.text) {
            return;
        }
        this.text.position.copy(this.owner.worldPosition);
        this.text.quaternion.copy(this.owner.worldOrientation);
        this.text.scale.copy(this.owner.worldScale);
        this.text.rotateY(Math.PI);
        this.text.updateMatrix();
    }
    // Apply the parameters from the facet to the text object
    applyParameters(facet) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        const params = facet.params || {};
        if (!this.text) {
            this.text = new Text();
        }
        this.text.gpuAccelerateSDF = true;
        switch (params.anchorX) {
            case TextAnchorX.Left:
                this.text.anchorX = "left";
                break;
            case TextAnchorX.Right:
                this.text.anchorX = "right";
                break;
            case TextAnchorX.Center:
            default:
                this.text.anchorX = "center";
                break;
        }
        switch (params.anchorY) {
            case TextAnchorY.Top:
                this.text.anchorY = "top";
                break;
            case TextAnchorY.Bottom:
                this.text.anchorY = "bottom";
                break;
            case TextAnchorY.Middle:
                this.text.anchorY = "middle";
                break;
            case TextAnchorY.TopBaseline:
                this.text.anchorY = "top-baseline";
                break;
            case TextAnchorY.TopCap:
                this.text.anchorY = "top-cap";
                break;
            case TextAnchorY.TopEx:
                this.text.anchorY = "top-ex";
                break;
            case TextAnchorY.BottomBaseline:
                this.text.anchorY = "bottom-baseline";
                break;
            default:
                this.text.anchorY = "middle";
        }
        switch (params.fontStyle) {
            case FontStyle.Italic:
                this.text.fontStyle = "italic";
                break;
            case FontStyle.Normal:
            default:
                this.text.fontStyle = "normal";
                break;
        }
        switch (params.fontWeight) {
            case FontWeight.Bold:
                this.text.fontWeight = "bold";
                break;
            case FontWeight.Normal:
            default:
                this.text.fontWeight = "normal";
                break;
        }
        switch (params.textAlign) {
            case TextAlign.Left:
                this.text.textAlign = "left";
                break;
            case TextAlign.Right:
                this.text.textAlign = "right";
                break;
            case TextAlign.Center:
            case undefined:
            default:
                this.text.textAlign = "center";
                break;
        }
        this.text.color = (_a = params.color) !== null && _a !== void 0 ? _a : 0xffffff;
        this.text.outlineColor = (_b = params.outlineColor) !== null && _b !== void 0 ? _b : 0x000000;
        this.text.strokeColor = (_c = params.strokeColor) !== null && _c !== void 0 ? _c : 0x000000;
        this.text.curveRadius = (_d = params.curveRadius) !== null && _d !== void 0 ? _d : 0;
        this.text.depthOffset = (_e = params.depthOffset) !== null && _e !== void 0 ? _e : 0;
        this.text.fillOpacity = (_f = params.fillOpacity) !== null && _f !== void 0 ? _f : 1;
        this.text.glyphGeometryDetail = (_g = params.glyphGeometryDetail) !== null && _g !== void 0 ? _g : 1;
        this.text.letterSpacing = (_h = params.letterSpacing) !== null && _h !== void 0 ? _h : 0;
        this.text.maxWidth = (_j = params.maxWidth) !== null && _j !== void 0 ? _j : Infinity;
        this.text.outlineBlur = (_k = params.outlineBlur) !== null && _k !== void 0 ? _k : 0;
        this.text.outlineOpacity = (_l = params.outlineOpacity) !== null && _l !== void 0 ? _l : 1;
        this.text.outlineWidth = (_m = params.outlineWidth) !== null && _m !== void 0 ? _m : 0;
        this.text.strokeOpacity = (_o = params.strokeOpacity) !== null && _o !== void 0 ? _o : 1;
        this.text.strokeWidth = (_p = params.strokeWidth) !== null && _p !== void 0 ? _p : 0;
        this.text.fontSize = params.fontSize ? params.fontSize : 0.5;
        if (Array.isArray(params.clipRect) && params.clipRect.length === 4) {
            // Maybe can be solved better
            const chunk = [0, 0, 0, 0];
            chunk[0] = params.clipRect[0];
            chunk[1] = params.clipRect[1];
            chunk[2] = params.clipRect[2];
            chunk[3] = params.clipRect[3];
            this.text.clipRect = chunk;
        }
        this.text.text = localizationService.getTranslation(facet.text);
        if (params.font)
            // URL to fontfile (*.ttf, *.otf, *.woff)
            this.text.font = params.font;
    }
}
TextRenderable.typestatic = "Text";
